import React, { useEffect, useMemo, useRef, useState } from 'react';
import './WriterControls.css';
import RichTextEditor from './RichTextEditor';
import { extractThumbnailSuggestions } from '../utils/thumbnailSuggestions';
import { isRichTextEmpty, normalizeRichTextContent } from '../utils/richText';
import ThumbnailPicker from './ThumbnailPicker';
import TagGroupInput from './TagGroupInput';
import VideoLinkField from './VideoLinkField';
import PublishDestination, { DESTINATION_ARTICLES, DESTINATION_VIDEOS } from './PublishDestination';
import { normalizeTagValue } from '../utils/tagUtils';
import { buildTagGroups, buildTagGroupsFromRaw } from '../utils/tagGroupUtils';
import { uploadMediaFile } from '../utils/directUpload';
import {
    VIDEO_NOT_STORED_MESSAGE,
    VIDEO_URL_ERROR,
    getVideoLinkKey,
    isVideoPost,
    parseVideoUrl,
} from '../utils/video';

const normalizeTagList = (rawTags) => {
    if (!rawTags) {
        return [];
    }

    const source = Array.isArray(rawTags)
        ? rawTags
        : (typeof rawTags === 'string'
            ? rawTags.split(',')
            : []);

    const unique = [];
    const seen = new Set();

    source.forEach((tag) => {
        if (tag == null) {
            return;
        }
        const normalized = normalizeTagValue(tag);
        if (!normalized || seen.has(normalized)) {
            return;
        }
        seen.add(normalized);
        unique.push(normalized);
    });

    return unique;
};

const buildTagGroupAssignments = (rawTags, rawTagGroups) => {
    const normalizedTags = normalizeTagList(rawTags);
    const sourceGroups = Array.isArray(rawTagGroups) ? rawTagGroups : [];
    const buckets = {
        subjects: [],
        geography: [],
        persons: [],
    };

    sourceGroups.forEach((group) => {
        if (!group) {
            return;
        }
        const category = typeof group.category === 'string' ? group.category.trim().toLowerCase() : '';
        if (category === 'geography') {
            buckets.geography.push(group);
            return;
        }
        if (category === 'persons') {
            buckets.persons.push(group);
            return;
        }
        buckets.subjects.push(group);
    });

    const groups = (() => {
        const groupMap = new Map();
        [...buildTagGroups(buckets.subjects), ...buildTagGroupsFromRaw(buckets.geography), ...buildTagGroupsFromRaw(buckets.persons)]
            .forEach((group) => {
                if (group?.id && !groupMap.has(group.id)) {
                    groupMap.set(group.id, group);
                }
            });
        return [...groupMap.values()];
    })();

    const tagToGroup = new Map();
    groups.forEach((group) => {
        const groupId = typeof group?.id === 'string' ? group.id : '';
        if (!groupId || !Array.isArray(group.subTags)) {
            return;
        }
        group.subTags.forEach((tag) => {
            const normalized = normalizeTagValue(tag);
            if (normalized && !tagToGroup.has(normalized)) {
                tagToGroup.set(normalized, groupId);
            }
        });
    });

    const assignments = new Map();
    const unassignedTags = [];

    normalizedTags.forEach((tag) => {
        const groupId = tagToGroup.get(tag);
        if (!groupId) {
            unassignedTags.push(tag);
            return;
        }
        const existing = assignments.get(groupId) || [];
        if (!existing.includes(tag)) {
            assignments.set(groupId, [...existing, tag]);
        }
    });

    const fallbackGroupId = groups[0]?.id;
    if (fallbackGroupId && unassignedTags.length > 0) {
        const existing = assignments.get(fallbackGroupId) || [];
        const merged = [...existing];
        unassignedTags.forEach((tag) => {
            if (!merged.includes(tag)) {
                merged.push(tag);
            }
        });
        assignments.set(fallbackGroupId, merged);
    }

    const nextAssignments = groups
        .map((group) => ({
            groupId: group.id,
            tags: assignments.get(group.id) || [],
        }))
        .filter((entry) => entry.tags.length > 0);

    return { tagGroups: nextAssignments, unassignedTags };
};

const buildFormState = (post, rawTagGroups) => {
    const rawContent = typeof post?.content === 'string' ? post.content : '';
    const rawBibliography = typeof post?.bibliography === 'string' ? post.bibliography : '';
    const { tagGroups, unassignedTags } = buildTagGroupAssignments(post?.tags, rawTagGroups);

    return {
        formState: {
        alias: post?.author || '',
        title: post?.title || '',
        description: post?.description || '',
        content: rawContent.trim().length > 0 ? normalizeRichTextContent(rawContent) : '',
        bibliography: rawBibliography.trim().length > 0 ? normalizeRichTextContent(rawBibliography) : '',
        thumbnail: typeof post?.thumbnail === 'string' ? post.thumbnail : '',
        destination: isVideoPost(post) ? DESTINATION_VIDEOS : DESTINATION_ARTICLES,
        videoUrl: isVideoPost(post) && typeof post.video.url === 'string' ? post.video.url : '',
        tagGroups,
        confidenceScoreRating: post?.confidenceScoreRating != null ? String(post.confidenceScoreRating) : '3',
        },
        unassignedTags,
    };
};

const WriterControls = ({
    post,
    onUpdate,
    onDelete,
    defaultPassphrase = '',
    defaultExpanded = false,
    adminToken = '',
    tagGroups = [],
}) => {
    const isAdmin = typeof adminToken === 'string' && adminToken.trim().length > 0;
    const [isOpen, setIsOpen] = useState(defaultExpanded);
    const initialForm = useMemo(() => buildFormState(post, tagGroups), [post, tagGroups]);
    const [formData, setFormData] = useState(initialForm.formState);
    const [passphrase, setPassphrase] = useState(defaultPassphrase);
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [tagNotice, setTagNotice] = useState('');
    const tagGroupsRef = useRef(initialForm.formState.tagGroups);
    const [updating, setUpdating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [videoMeta, setVideoMeta] = useState(null);
    const formPostIdRef = useRef(null);

    useEffect(() => {
        if (isOpen && post) {
            const { formState, unassignedTags } = buildFormState(post, tagGroups);
            setFormData(formState);
            tagGroupsRef.current = formState.tagGroups;
            setVideoMeta(null);
            // The refreshed copy of the post that a save hands back keeps the
            // success message (it names where the document is now listed).
            if (formPostIdRef.current !== post._id) {
                setStatus('');
                setError('');
                setConfirmDelete(false);
            }
            formPostIdRef.current = post._id;

            if (Array.isArray(unassignedTags) && unassignedTags.length > 0) {
                setTagNotice('Some existing sub-tags were not yet assigned to a main tag. Review the groups before saving.');
            } else {
                setTagNotice('');
            }
        } else if (!isOpen) {
            formPostIdRef.current = null;
        }
    }, [post, isOpen, tagGroups]);

    useEffect(() => {
        setConfirmDelete(false);
    }, [passphrase]);

    useEffect(() => {
        setPassphrase(defaultPassphrase);
    }, [defaultPassphrase]);

    useEffect(() => {
        if (defaultExpanded) {
            setIsOpen(true);
        }
    }, [defaultExpanded]);

    const handleToggle = () => {
        setIsOpen((prev) => !prev);
        if (!isOpen) {
            setPassphrase(defaultPassphrase);
            setStatus('');
            setError('');
            setConfirmDelete(false);
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleContentChange = (nextValue) => {
        setFormData((prev) => ({
            ...prev,
            content: nextValue,
        }));
    };

    const handleBibliographyChange = (nextValue) => {
        setFormData((prev) => ({
            ...prev,
            bibliography: nextValue,
        }));
    };

    const handleTagGroupsChange = (nextValue) => {
        const nextGroups = Array.isArray(nextValue) ? nextValue : [];
        tagGroupsRef.current = nextGroups;
        setFormData((prev) => ({
            ...prev,
            tagGroups: nextGroups,
        }));
    };

    // The editors and the thumbnail picker report upload failures
    // themselves, next to the upload (a per-file summary; a failed automatic
    // video poster is skipped silently), so uploads leave the form-level
    // error alone.
    const handleMediaUpload = async (file) => {
        const trimmedPassphrase = passphrase?.trim();
        if (!isAdmin && !trimmedPassphrase) {
            throw new Error('Writer passphrase is required before uploading media');
        }
        return uploadMediaFile(file, { adminToken, passphrase: trimmedPassphrase });
    };

    const handleThumbnailChange = (nextValue) => {
        setFormData((prev) => ({
            ...prev,
            thumbnail: nextValue,
        }));
    };

    const handleDestinationChange = (nextDestination) => {
        setFormData((prev) => ({
            ...prev,
            destination: nextDestination,
        }));
    };

    const handleVideoUrlChange = (nextValue) => {
        setFormData((prev) => ({
            ...prev,
            videoUrl: nextValue,
        }));
    };

    // Existing documents keep their title; only an empty one is prefilled
    // from the video.
    const handleVideoPreview = (meta) => {
        setVideoMeta(meta);
        if (meta?.title) {
            setFormData((prev) => (
                prev.title.trim() ? prev : { ...prev, title: meta.title.slice(0, 200) }
            ));
        }
    };

    const isVideoDestination = formData.destination === DESTINATION_VIDEOS;

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!onUpdate) {
            return;
        }

        if (!isAdmin && !passphrase.trim()) {
            setError('Writer passphrase is required');
            return;
        }

        // Condoned Media: a video post needs a playable link; moving a video
        // post back to Articles sends an empty link, which removes the video.
        let videoPayload = {};
        if (isVideoDestination) {
            const trimmedVideoUrl = (formData.videoUrl || '').trim();
            const parsedVideo = parseVideoUrl(trimmedVideoUrl);
            if (!trimmedVideoUrl) {
                setError('Paste the YouTube or Rumble link of the video for the Condoned Media page');
                return;
            }
            if (!parsedVideo) {
                setError(VIDEO_URL_ERROR);
                return;
            }
            videoPayload = {
                videoUrl: trimmedVideoUrl,
                ...(videoMeta && videoMeta.key === getVideoLinkKey(parsedVideo)
                    ? { videoTitle: videoMeta.title, videoChannel: videoMeta.channel }
                    : {}),
            };
        } else if (isVideoPost(post)) {
            videoPayload = { videoUrl: '' };
        }

        const sourceTagGroups = (Array.isArray(tagGroupsRef.current) && tagGroupsRef.current.length > 0)
            ? tagGroupsRef.current
            : (Array.isArray(formData.tagGroups) ? formData.tagGroups : []);

        const tagGroupPayload = sourceTagGroups
            .map((entry) => {
                const groupId = typeof entry?.groupId === 'string'
                    ? entry.groupId.trim()
                    : (typeof entry?.id === 'string' ? entry.id.trim() : '');
                const tags = Array.isArray(entry?.tags) ? entry.tags : [];
                const normalizedTags = tags
                    .map((tag) => normalizeTagValue(tag))
                    .filter(Boolean)
                    .filter((tag, index, array) => array.indexOf(tag) === index);
                return { groupId, tags: normalizedTags };
            })
            .filter((entry) => entry.groupId && Array.isArray(entry.tags) && entry.tags.length > 0);

        const flattenedTags = tagGroupPayload.flatMap((entry) => entry.tags);
        const uniqueFlattenedTags = [...new Set(flattenedTags)];

        if (uniqueFlattenedTags.length === 0) {
            if (process.env.NODE_ENV !== 'production') {
                console.debug('[WriterControls] No tags detected. sourceTagGroups=', sourceTagGroups);
                console.debug('[WriterControls] Computed tagGroupPayload=', tagGroupPayload);
            }
            setError('Select at least one sub-tag under a main tag');
            return;
        }

        setUpdating(true);
        setError('');
        setStatus('');

        try {
            const {
                confidenceScoreRating,
                thumbnail,
                content,
                bibliography,
                destination,
                videoUrl,
                ...rest
            } = formData;
            const normalizedContent = normalizeRichTextContent(content);
            const normalizedBibliography = normalizeRichTextContent(bibliography);
            const contentToSend = isRichTextEmpty(normalizedContent) ? '' : normalizedContent;
            const bibliographyToSend = isRichTextEmpty(normalizedBibliography) ? '' : normalizedBibliography;
            const trimmedThumbnail = typeof thumbnail === 'string' ? thumbnail.trim() : '';
            const payload = {
                ...rest,
                thumbnail: trimmedThumbnail,
                content: contentToSend,
                bibliography: bibliographyToSend,
                confidenceScoreRating: Number(confidenceScoreRating),
                tagGroups: tagGroupPayload,
                ...videoPayload,
                ...(isAdmin ? { adminToken: adminToken.trim() } : { passphrase: passphrase.trim() }),
            };

            const updatedPost = await onUpdate(payload);
            // Report where the server actually filed it: a backend without
            // Condoned Media support saves the document but drops the link.
            if (updatedPost && destination === DESTINATION_VIDEOS && !isVideoPost(updatedPost)) {
                setError(VIDEO_NOT_STORED_MESSAGE);
                setConfirmDelete(false);
                return;
            }
            const destinationLabel = destination === DESTINATION_VIDEOS
                ? 'listed on the Condoned Media — Videos page (/videos)'
                : 'listed under Articles';
            setStatus(updatedPost
                ? `Document updated successfully — ${destinationLabel}`
                : `Document updated — ${destinationLabel}`);
            setConfirmDelete(false);
        } catch (submissionError) {
            setError(submissionError.message || 'Failed to update document');
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async () => {
        if (!onDelete) {
            return;
        }

        if (!isAdmin && !passphrase.trim()) {
            setError('Writer passphrase is required');
            return;
        }

        if (!confirmDelete) {
            setConfirmDelete(true);
            setStatus('Press delete again to confirm');
            return;
        }

        setDeleting(true);
        setError('');
        setStatus('');

        try {
            await onDelete(isAdmin ? { adminToken: adminToken.trim() } : { passphrase: passphrase.trim() });
        } catch (submissionError) {
            setError(submissionError.message || 'Failed to delete document');
        } finally {
            setDeleting(false);
        }
    };

    if (!post) {
        return null;
    }

    return (
        <section className="writer-controls">
            <button
                type="button"
                className="writer-controls-toggle"
                onClick={handleToggle}
            >
                {isOpen ? 'Finish Editing' : 'Edit Document'}
            </button>

            {isOpen && (
                <div className="writer-controls-panel">
                    <header className="writer-controls-header">
                        <h3>Writer Controls</h3>
                        <p>{isAdmin ? 'Admin access: edit any document.' : 'Update or remove this document using your writer passphrase.'}</p>
                    </header>

                    <form className="writer-controls-form" onSubmit={handleSubmit}>
                        {error && <div className="writer-controls-alert error">[ERROR] {error}</div>}
                        {status && <div className="writer-controls-alert status">{status}</div>}

                        {!isAdmin && (
                            <label className="writer-controls-field">
                                <span>Writer Passphrase *</span>
                                <input
                                    type="password"
                                    name="passphrase"
                                    value={passphrase}
                                    onChange={(event) => setPassphrase(event.target.value)}
                                    placeholder="Enter your writer passphrase"
                                    autoComplete="off"
                                    required
                                />
                            </label>
                        )}

                        <label className="writer-controls-field">
                            <span>Alias</span>
                            <input
                                type="text"
                                name="alias"
                                value={formData.alias}
                                onChange={handleChange}
                                placeholder="Update displayed alias"
                                maxLength={30}
                            />
                        </label>

                        <div className="writer-controls-field">
                            <PublishDestination
                                value={formData.destination}
                                onChange={handleDestinationChange}
                                disabled={updating || deleting}
                                hint={isVideoDestination
                                    ? 'Listed on /videos (not in the article listing). Switching back to Articles removes the video from this document.'
                                    : 'Listed with the articles on the front page.'}
                            />
                        </div>

                        {isVideoDestination && (
                            <div className="writer-controls-field">
                                <VideoLinkField
                                    value={formData.videoUrl}
                                    onChange={handleVideoUrlChange}
                                    onPreview={handleVideoPreview}
                                    storedVideo={isVideoPost(post) ? post.video : null}
                                    disabled={updating || deleting}
                                    required
                                />
                            </div>
                        )}

                        <label className="writer-controls-field">
                            <span>Document Title *</span>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                maxLength={200}
                            />
                        </label>

                        <label className="writer-controls-field">
                            <span>Description *</span>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={4}
                                maxLength={1000}
                                required
                            />
                        </label>

                        <div className="writer-controls-field">
                            <ThumbnailPicker
                                value={formData.thumbnail}
                                onChange={handleThumbnailChange}
                                onUploadImage={handleMediaUpload}
                                label="THUMBNAIL (OPTIONAL)"
                                hint={isVideoDestination
                                    ? 'Shown on the video\'s card on the Videos page. Leave it empty to use the video\'s own thumbnail.'
                                    : 'Displayed above the title on the home page.'}
                                aspect={16 / 9}
                                suggestions={extractThumbnailSuggestions(formData.content)}
                            />
                        </div>

                        {/* Not <label>: a label forwards clicks on its text to its
                            first control, the editor's ¶ button, which would turn
                            the block under the cursor into a paragraph. */}
                        <div className="writer-controls-field">
                            <span>Full Content</span>
                            <RichTextEditor
                                className="writer-controls-editor"
                                value={formData.content}
                                onChange={handleContentChange}
                                placeholder="Update the full document content"
                                onUploadImage={handleMediaUpload}
                            />
                            <span className="field-hint">Add photos, video and audio with the toolbar — several at once, by paste, or by drag and drop. Large photos are downscaled automatically; each can be up to 10MB (iPhone HEIC photos need exporting as JPEG first).</span>
                        </div>

                        <div className="writer-controls-field">
                            <span>Bibliography / Sources</span>
                            <RichTextEditor
                                className="writer-controls-editor writer-controls-editor--bibliography"
                                value={formData.bibliography}
                                onChange={handleBibliographyChange}
                                placeholder="List references, citations, or related reading (optional)"
                                onUploadImage={handleMediaUpload}
                            />
                            <span className="field-hint">Share citations, links, or further reading to support the document.</span>
                        </div>

                        <div className="writer-controls-field">
                            <span>Main Tags &amp; Sub-Tags *</span>
                            <TagGroupInput
                                groups={tagGroups}
                                value={formData.tagGroups}
                                onChange={handleTagGroupsChange}
                                disabled={updating || deleting}
                                required
                            />
                            {tagNotice && (
                                <span className="field-hint">{tagNotice}</span>
                            )}
                        </div>

                        <label className="writer-controls-field">
                            <span>Confidence Score *</span>
                            <select
                                name="confidenceScoreRating"
                                value={formData.confidenceScoreRating}
                                onChange={handleChange}
                                required
                            >
                                <option value="1">1 - Speculative</option>
                                <option value="2">2 - Emerging Narrative</option>
                                <option value="3">3 - Evidence-Based Claim</option>
                                <option value="4">4 - Mainstream Facts with Alternative Framing</option>
                                <option value="5">5 - Established Consensus or Ideology</option>
                            </select>
                        </label>

                        <div className="writer-controls-actions">
                            <button
                                type="submit"
                                className="writer-controls-submit"
                                disabled={updating}
                            >
                                {updating ? 'Updating...' : 'Update Document'}
                            </button>
                            <button
                                type="button"
                                className="writer-controls-delete"
                                onClick={handleDelete}
                                disabled={deleting}
                            >
                                {deleting ? 'Deleting...' : confirmDelete ? 'Confirm Delete' : 'Delete Document'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </section>
    );
};

export default WriterControls;
